import BuyFlowIcon from "@components/BuyForm/Icon";
import { pxToRem } from "@helpers/buyFlowTextStyles";
import { addOpacityToColor, colours } from "@helpers/colours";
import { mediaQuery } from "@helpers/deviceDimensions";
import React, { useEffect, useRef } from "react";
import styled from "styled-components";

const HEADER_HEIGHT_MOBILE = pxToRem(53);

const CloseButtonContainer = styled.button`
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  width: calc(${HEADER_HEIGHT_MOBILE} - 1.5rem);
  min-width: calc(${HEADER_HEIGHT_MOBILE} - 1.5rem);
  height: calc(${HEADER_HEIGHT_MOBILE} - 1.5rem);
  background: transparent;
  border-radius: 50%;
  cursor: pointer;
  border: none !important;
  padding: 0;
  &:hover {
    border: none !important;
  }
  
  &:focus {
    background-color: ${addOpacityToColor(colours.PEACH, 0.15)};
  }
  display: flex;
  align-items: center;
  justify-content: center;

  i {
    font-size: 3rem;
  }

  ${mediaQuery.tabletAndUp} {
    right: 1rem;
    width: 4rem;
    min-width: 4rem;
    height: 4rem;

    i {
      font-size: 4rem;
    }
  }

  :hover,
  :active {
    background-color: ${addOpacityToColor(colours.PEACH, 0.15)};
  }
`;

const CloseButton = React.forwardRef(({ onClick, className, autoFocus }, ref) => {
  return (
    <CloseButtonContainer
      onClick={onClick}
      className={className}
      aria-label="Close"
      autoFocus={autoFocus}
      ref={ref}
    >
      <BuyFlowIcon iconName="close" />
    </CloseButtonContainer>
  );
});

const MODAL_SIDE_GUTTER = "40px";

const DialogBox = styled.dialog`
  position: fixed;
  top: 100px;
  left: 50%;
  transform: translateX(-50%);
  margin: 0;
  box-sizing: border-box;
  width: min(500px, calc(100% - ${MODAL_SIDE_GUTTER}));
  background-color: ${colours.PALE_PEACH};
  padding: 50px 25px;
  border-radius: 15px;
  border: none;
  outline: none;
  max-height: calc(100vh - 100px);
  overflow: auto;
  ${(props) =>
		!props.noMaxWidth &&
		`
    @media (min-width: 890px) {
      width: min(869px, calc(100% - ${MODAL_SIDE_GUTTER}));
      padding: 60px 50px;
    }
  `}
  &::backdrop {
    background-color: rgba(0, 0, 0, 0.5);
  }
`;

const ModalOverlay = ({ show, onClose, noMaxWidth, children }) => {
  const dialogRef = useRef(null);
  const closeButtonRef = useRef(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return undefined;

    if (show) {
      if (!dialog.open) {
        const scrollX = window.scrollX;
        const scrollY = window.scrollY;
        dialog.showModal();
        closeButtonRef.current?.focus();
        window.scrollTo(scrollX, scrollY);
      }
    } else {
      if (dialog.open) {
        const scrollX = window.scrollX;
        const scrollY = window.scrollY;
        dialog.close();
        window.scrollTo(scrollX, scrollY);
      }
    }
  }, [show]);

  const handleBackdropClick = (e) => {
    if (e.target === dialogRef.current) {
      onClose();
    }
  };

  const handleFocusTrap = (e) => {
    if (e.key !== "Tab") return;

    const focusable = Array.from(
      dialogRef.current.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    );

    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus(); 
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  };

  return (
    <DialogBox
      noMaxWidth={noMaxWidth}
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      tabIndex={-1}
      onClick={handleBackdropClick}
      onKeyDown={handleFocusTrap}
      onCancel={(e) => { e.preventDefault(); onClose(); }}
    >
      <CloseButton ref={closeButtonRef} onClick={onClose} />
      {children}
    </DialogBox>
  );
};

export default ModalOverlay;